import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Typography,
  Table,
  Tag,
  Progress,
  Tabs,
  List,
  Avatar,
  Alert,
  DatePicker,
  Select,
  Input,
  Modal,
  Form,
  message,
  Tooltip,
  Badge,
  Timeline,
  Steps,
  Divider,
  InputNumber,
  Descriptions
} from 'antd';
import {
  ToolOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  StopOutlined,
  BugOutlined,
  TeamOutlined,
  CalendarOutlined,
  BarChartOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
  RocketOutlined,
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  DownloadOutlined,
  AlertOutlined,
  UserOutlined,
  BuildOutlined,
  ControlOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;
const { TabPane } = Tabs;
const { Step } = Steps;
const { Option } = Select;

interface BOM {
  id: string;
  name: string;
  product: { itemName: string; itemCode: string };
  version: string;
  quantity: number;
  items: Array<{
    product: { itemName: string; itemCode: string };
    quantity: number;
    uom: string;
  }>;
  totalCost: number;
  isDefault: boolean;
}

interface WorkOrder {
  id: string;
  workOrderNumber: string;
  product: { itemName: string; itemCode: string };
  quantity: number;
  status: string;
  priority: string;
  plannedStartDate: string;
  plannedEndDate: string;
  actualStartDate?: string;
  actualEndDate?: string;
  progress: number;
}

interface ProductionPlan {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: string;
  items: Array<{
    product: { itemName: string };
    plannedQty: number;
    producedQty: number;
  }>;
}

const ManufacturingOverview: React.FC = () => {
  const location = useLocation();
  
  // Set active tab based on current route
  const getActiveTabFromRoute = () => {
    const path = location.pathname;
    if (path.includes('/manufacturing/bom')) return 'bom';
    if (path.includes('/manufacturing/work-orders')) return 'work_orders';
    if (path.includes('/manufacturing/production-plans')) return 'production_plans';
    if (path.includes('/manufacturing/quality')) return 'quality';
    if (path.includes('/manufacturing/lines')) return 'production_lines';
    return 'overview';
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTabFromRoute());
  const [boms, setBOMs] = useState<BOM[]>([]);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [productionPlans, setProductionPlans] = useState<ProductionPlan[]>([]);
  const [isBOMModalVisible, setIsBOMModalVisible] = useState(false);
  const [isWorkOrderModalVisible, setIsWorkOrderModalVisible] = useState(false);
  const [isPlanModalVisible, setIsPlanModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  // Update active tab when route changes
  useEffect(() => {
    setActiveTab(getActiveTabFromRoute());
  }, [location.pathname]);

  // Mock data for demonstration
  useEffect(() => {
    setBOMs([
      {
        id: '1',
        name: 'Standard Laptop BOM',
        product: { itemName: 'Gaming Laptop Pro', itemCode: 'LAPTOP-001' },
        version: '1.0',
        quantity: 1,
        items: [
          { product: { itemName: 'CPU Intel i7', itemCode: 'CPU-001' }, quantity: 1, uom: 'Nos' },
          { product: { itemName: 'RAM 16GB', itemCode: 'RAM-001' }, quantity: 2, uom: 'Nos' },
          { product: { itemName: 'SSD 512GB', itemCode: 'SSD-001' }, quantity: 1, uom: 'Nos' }
        ],
        totalCost: 850.00,
        isDefault: true
      }
    ]);

    setWorkOrders([
      {
        id: '1',
        workOrderNumber: 'WO-202406-0001',
        product: { itemName: 'Gaming Laptop Pro', itemCode: 'LAPTOP-001' },
        quantity: 50,
        status: 'IN_PROGRESS',
        priority: 'HIGH',
        plannedStartDate: '2024-06-24',
        plannedEndDate: '2024-06-30',
        actualStartDate: '2024-06-24',
        progress: 65
      },
      {
        id: '2',
        workOrderNumber: 'WO-202406-0002',
        product: { itemName: 'Office Laptop Standard', itemCode: 'LAPTOP-002' },
        quantity: 30,
        status: 'DRAFT',
        priority: 'MEDIUM',
        plannedStartDate: '2024-07-01',
        plannedEndDate: '2024-07-05',
        progress: 0
      }
    ]);

    setProductionPlans([
      {
        id: '1',
        name: 'Q3 2024 Production Plan',
        startDate: '2024-07-01',
        endDate: '2024-09-30',
        status: 'ACTIVE',
        items: [
          { product: { itemName: 'Gaming Laptop Pro' }, plannedQty: 200, producedQty: 65 },
          { product: { itemName: 'Office Laptop Standard' }, plannedQty: 150, producedQty: 0 }
        ]
      }
    ]);
  }, []);

  const bomColumns: ColumnsType<BOM> = [
    {
      title: 'BOM Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Product',
      key: 'product',
      render: (_, record) => `${record.product.itemName} (${record.product.itemCode})`,
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: 'Total Cost',
      dataIndex: 'totalCost',
      key: 'totalCost',
      render: (cost) => `$${cost.toFixed(2)}`,
    },
    {
      title: 'Default',
      dataIndex: 'isDefault',
      key: 'isDefault',
      render: (isDefault) => (
        <Badge status={isDefault ? 'success' : 'default'} text={isDefault ? 'Yes' : 'No'} />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: () => (
        <Space>
          <Button size="small">View</Button>
          <Button size="small">Edit</Button>
          <Button size="small">Copy</Button>
        </Space>
      ),
    },
  ];

  const workOrderColumns: ColumnsType<WorkOrder> = [
    {
      title: 'Work Order',
      dataIndex: 'workOrderNumber',
      key: 'workOrderNumber',
    },
    {
      title: 'Product',
      key: 'product',
      render: (_, record) => `${record.product.itemName} (${record.product.itemCode})`,
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = {
          DRAFT: 'default',
          IN_PROGRESS: 'processing',
          COMPLETED: 'success',
          CANCELLED: 'error'
        };
        return <Badge status={colors[status as keyof typeof colors]} text={status.replace('_', ' ')} />;
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => {
        const colors = {
          LOW: 'green',
          MEDIUM: 'blue',
          HIGH: 'orange',
          URGENT: 'red'
        };
        return <Tag color={colors[priority as keyof typeof colors]}>{priority}</Tag>;
      },
    },
    {
      title: 'Progress',
      dataIndex: 'progress',
      key: 'progress',
      render: (progress) => <Progress percent={progress} size="small" />,
    },
    {
      title: 'Start Date',
      dataIndex: 'plannedStartDate',
      key: 'plannedStartDate',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.status === 'DRAFT' && <Button size="small" type="primary">Start</Button>}
          {record.status === 'IN_PROGRESS' && <Button size="small">Pause</Button>}
          <Button size="small">View</Button>
        </Space>
      ),
    },
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'DRAFT': return <PauseCircleOutlined />;
      case 'IN_PROGRESS': return <PlayCircleOutlined />;
      case 'COMPLETED': return <CheckCircleOutlined />;
      default: return <ClockCircleOutlined />;
    }
  };

  const renderOverview = () => (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Work Orders"
              value={workOrders.filter(wo => wo.status === 'IN_PROGRESS').length}
              prefix={<PlayCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total BOMs"
              value={boms.length}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Production Efficiency"
              value={87.5}
              suffix="%"
              prefix={<BarChartOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="On-Time Delivery"
              value={92.3}
              suffix="%"
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card title="Production Schedule" style={{ height: 400 }}>
            <Timeline>
              {workOrders.slice(0, 5).map(wo => (
                <Timeline.Item 
                  key={wo.id}
                  dot={getStatusIcon(wo.status)}
                  color={wo.status === 'IN_PROGRESS' ? 'blue' : wo.status === 'COMPLETED' ? 'green' : 'gray'}
                >
                  <div>
                    <strong>{wo.workOrderNumber}</strong> - {wo.product.itemName}
                    <br />
                    <span style={{ color: '#999' }}>
                      Qty: {wo.quantity} | Status: {wo.status} | Progress: {wo.progress}%
                    </span>
                    <br />
                    <span style={{ color: '#999', fontSize: '12px' }}>
                      {wo.plannedStartDate} - {wo.plannedEndDate}
                    </span>
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="Production Metrics" style={{ height: 400 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>Capacity Utilization</span>
                  <span>78%</span>
                </div>
                <Progress percent={78} strokeColor="#1890ff" />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>Quality Rate</span>
                  <span>95%</span>
                </div>
                <Progress percent={95} strokeColor="#52c41a" />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>Equipment Efficiency</span>
                  <span>88%</span>
                </div>
                <Progress percent={88} strokeColor="#faad14" />
              </div>
              <Divider />
              <div style={{ textAlign: 'center' }}>
                <h3>Overall Equipment Effectiveness</h3>
                <Progress 
                  type="circle" 
                  percent={85} 
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                />
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1>Manufacturing</h1>
        <Space>
          <Button 
            icon={<PlusOutlined />}
            onClick={() => setIsBOMModalVisible(true)}
          >
            New BOM
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => setIsWorkOrderModalVisible(true)}
          >
            New Work Order
          </Button>
        </Space>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Overview" key="overview">
          {renderOverview()}
        </TabPane>
        
        <TabPane tab="Bill of Materials" key="bom">
          <Card>
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Input.Search placeholder="Search BOMs..." style={{ width: 300 }} />
                <Select placeholder="Product" style={{ width: 200 }}>
                  <Option value="laptop-001">Gaming Laptop Pro</Option>
                  <Option value="laptop-002">Office Laptop Standard</Option>
                </Select>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />}
                  onClick={() => setIsBOMModalVisible(true)}
                >
                  New BOM
                </Button>
              </Space>
            </div>
            <Table 
              columns={bomColumns} 
              dataSource={boms}
              rowKey="id"
              expandable={{
                expandedRowRender: (record) => (
                  <div style={{ padding: 16 }}>
                    <h4>BOM Items:</h4>
                    <Table
                      size="small"
                      columns={[
                        { title: 'Item', dataIndex: ['product', 'itemName'], key: 'item' },
                        { title: 'Item Code', dataIndex: ['product', 'itemCode'], key: 'itemCode' },
                        { title: 'Quantity', dataIndex: 'quantity', key: 'quantity' },
                        { title: 'UOM', dataIndex: 'uom', key: 'uom' },
                      ]}
                      dataSource={record.items}
                      pagination={false}
                      rowKey={(item, index) => index?.toString() || '0'}
                    />
                  </div>
                ),
              }}
            />
          </Card>
        </TabPane>

        <TabPane tab="Work Orders" key="workorders">
          <Card>
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Input.Search placeholder="Search work orders..." style={{ width: 300 }} />
                <Select placeholder="Status" style={{ width: 150 }}>
                  <Option value="DRAFT">Draft</Option>
                  <Option value="IN_PROGRESS">In Progress</Option>
                  <Option value="COMPLETED">Completed</Option>
                  <Option value="CANCELLED">Cancelled</Option>
                </Select>
                <Select placeholder="Priority" style={{ width: 120 }}>
                  <Option value="LOW">Low</Option>
                  <Option value="MEDIUM">Medium</Option>
                  <Option value="HIGH">High</Option>
                  <Option value="URGENT">Urgent</Option>
                </Select>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />}
                  onClick={() => setIsWorkOrderModalVisible(true)}
                >
                  New Work Order
                </Button>
              </Space>
            </div>
            <Table 
              columns={workOrderColumns} 
              dataSource={workOrders}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>

        <TabPane tab="Production Planning" key="planning">
          <Card>
            <div style={{ marginBottom: 16 }}>
              <Space>
                <RangePicker />
                <Select placeholder="Status" style={{ width: 120 }}>
                  <Option value="DRAFT">Draft</Option>
                  <Option value="ACTIVE">Active</Option>
                  <Option value="COMPLETED">Completed</Option>
                </Select>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />}
                  onClick={() => setIsPlanModalVisible(true)}
                >
                  New Production Plan
                </Button>
              </Space>
            </div>
            
            {productionPlans.map(plan => (
              <Card key={plan.id} style={{ marginBottom: 16 }}>
                <Descriptions title={plan.name} extra={<Tag color="blue">{plan.status}</Tag>}>
                  <Descriptions.Item label="Start Date">{plan.startDate}</Descriptions.Item>
                  <Descriptions.Item label="End Date">{plan.endDate}</Descriptions.Item>
                  <Descriptions.Item label="Items">{plan.items.length}</Descriptions.Item>
                </Descriptions>
                
                <div style={{ marginTop: 16 }}>
                  <h4>Production Items:</h4>
                  {plan.items.map((item, index) => (
                    <div key={index} style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{item.product.itemName}</span>
                        <span>{item.producedQty} / {item.plannedQty}</span>
                      </div>
                      <Progress 
                        percent={Math.round((item.producedQty / item.plannedQty) * 100)} 
                        size="small"
                        status={item.producedQty >= item.plannedQty ? 'success' : 'active'}
                      />
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </Card>
        </TabPane>

        <TabPane tab="Quality Control" key="quality">
          <Card title="Quality Control Dashboard">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Card>
                  <Statistic
                    title="Quality Rate"
                    value={95.2}
                    suffix="%"
                    prefix={<CheckCircleOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card>
                  <Statistic
                    title="Defect Rate"
                    value={2.8}
                    suffix="%"
                    prefix={<AlertOutlined />}
                    valueStyle={{ color: '#f5222d' }}
                  />
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card>
                  <Statistic
                    title="Inspections Today"
                    value={24}
                    prefix={<SettingOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
            </Row>
            
            <Divider />
            
            <div>
              <h3>Recent Quality Issues</h3>
              <Timeline>
                <Timeline.Item color="red">
                  <p>Defective components found in Batch #B001</p>
                  <p style={{ color: '#999', fontSize: '12px' }}>2 hours ago</p>
                </Timeline.Item>
                <Timeline.Item color="green">
                  <p>Quality inspection passed for Work Order WO-202406-0001</p>
                  <p style={{ color: '#999', fontSize: '12px' }}>4 hours ago</p>
                </Timeline.Item>
                <Timeline.Item>
                  <p>New quality checklist created for Gaming Laptop Pro</p>
                  <p style={{ color: '#999', fontSize: '12px' }}>Yesterday</p>
                </Timeline.Item>
              </Timeline>
            </div>
          </Card>
        </TabPane>
      </Tabs>

      {/* BOM Modal */}
      <Modal
        title="Create New BOM"
        open={isBOMModalVisible}
        onCancel={() => {
          setIsBOMModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="BOM Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="productId" label="Product" rules={[{ required: true }]}>
                <Select placeholder="Select Product">
                  <Option value="laptop-001">Gaming Laptop Pro</Option>
                  <Option value="laptop-002">Office Laptop Standard</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="version" label="Version">
                <Input defaultValue="1.0" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="quantity" label="Quantity">
                <InputNumber min={1} defaultValue={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          
          <Divider>BOM Items</Divider>
          
          <Form.List name="items">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row key={key} gutter={16} align="middle">
                    <Col span={8}>
                      <Form.Item
                        {...restField}
                        name={[name, 'productId']}
                        rules={[{ required: true, message: 'Missing product' }]}
                      >
                        <Select placeholder="Select Product">
                          <Option value="cpu-001">CPU Intel i7</Option>
                          <Option value="ram-001">RAM 16GB</Option>
                          <Option value="ssd-001">SSD 512GB</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        {...restField}
                        name={[name, 'quantity']}
                        rules={[{ required: true, message: 'Missing quantity' }]}
                      >
                        <InputNumber placeholder="Quantity" min={1} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        {...restField}
                        name={[name, 'uom']}
                      >
                        <Select defaultValue="Nos">
                          <Option value="Nos">Nos</Option>
                          <Option value="Kg">Kg</Option>
                          <Option value="Meter">Meter</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Button onClick={() => remove(name)} danger>
                        Remove
                      </Button>
                    </Col>
                  </Row>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add BOM Item
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Create BOM
              </Button>
              <Button onClick={() => {
                setIsBOMModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ManufacturingOverview; 